import { useState, useEffect, useRef } from 'react';
import { Button } from '../../../components/ui/button';
import { Plus, Merge, Split, Minus } from 'lucide-react';

export const DEFAULT_BUDGET_TABLE_STRUCTURE: string[][] = [
  ["Audience Segment", "Media Channel", "Flight Dates", "Budget", "CPM", "Impressions"],
  ["", "", "", "", "", ""],
  ["", "", "", "", "", ""],
];

/** Next or previous editable budget cell, skipping vertically merged continuations. */
export function findAdjacentBudgetCell(
  data: string[][],
  row: number,
  col: number,
  direction: 1 | -1
): { row: number; col: number } | null {
  const rowCount = data.length;
  const colCount = data[0]?.length ?? 0;
  if (rowCount === 0 || colCount === 0) {
    return null;
  }

  let nextRow = row;
  let nextCol = col + direction;

  while (nextRow >= 0 && nextRow < rowCount) {
    while (nextCol >= 0 && nextCol < colCount) {
      if (data[nextRow][nextCol] !== '$MERGE') {
        return { row: nextRow, col: nextCol };
      }
      nextCol += direction;
    }
    nextRow += direction;
    nextCol = direction === 1 ? 0 : colCount - 1;
  }

  return null;
}

interface InsertionOrderTableProps {
  initialData?: string[][];
  onChange: (data: string[][]) => void;
  disabled?: boolean;
}

export const InsertionOrderTable = ({
  initialData,
  onChange,
  disabled = false,
}: InsertionOrderTableProps) => {
  const [tableData, setTableData] = useState<string[][]>(() => {
    if (initialData && initialData.length > 0) {
      return initialData;
    }
    return DEFAULT_BUDGET_TABLE_STRUCTURE;
  });

  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  const [editingCell, setEditingCell] = useState<{ row: number; col: number } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ row: number; col: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const skipBlurCommitRef = useRef(false);

  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      // Set cursor at end instead of selecting all text
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(length, length);
    }
  }, [editingCell]);

  useEffect(() => {
    if (initialData && initialData.length > 0) {
      setTableData(initialData);
    } else {
      setTableData(DEFAULT_BUDGET_TABLE_STRUCTURE);
    }
  }, [initialData]);

  // Only call onChange when user actually modifies the table, not during initialization
  const updateTableData = (newData: string[][]) => {
    setTableData(newData);
    onChange(newData);
  };

  const getMergedCellGroup = (row: number, col: number): Set<string> => {
    const cells = new Set<string>();
    // If this is a merged cell, find its parent
    if (isCellMerged(row, col)) {
      for (let r = row - 1; r >= 0; r--) {
        if (tableData[r][col] !== '$MERGE') {
          return getMergedCellGroup(r, col);
        }
      }
    }
    // Else this is a parent cell, add it and all its merged children
    else {
      cells.add(`${row}-${col}`);
      for (let r = row + 1; r < tableData.length; r++) {
        if (tableData[r][col] === '$MERGE') {
          cells.add(`${r}-${col}`);
        } else {
          break;
        }
      }
    }

    return cells;
  };

  const handleMouseDown = (row: number, col: number, event: React.MouseEvent) => {
    if (disabled || event.button !== 0) return;
    event.preventDefault();
    setIsDragging(true);
    setDragStart({ row, col });
    setSelectedCells(new Set([`${row}-${col}`]));
  };

  const handleMouseEnter = (row: number, col: number) => {
    if (!isDragging || !dragStart || disabled) return;
    // Only allow vertical selection (same column)
    if (col !== dragStart.col) return;
    const minRow = Math.min(row, dragStart.row);
    const maxRow = Math.max(row, dragStart.row);
    const newSelected = new Set<string>();
    for (let r = minRow; r <= maxRow; r++) {
      // Only add the specific cell, not merged groups
      newSelected.add(`${r}-${col}`);
    }
    setSelectedCells(newSelected);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragStart(null);
  };

  const handleCellClick = (row: number, col: number) => {
    if (disabled || isDragging) return;
    // If currently editing a different cell, save it first
    if (editingCell && (editingCell.row !== row || editingCell.col !== col)) {
      const newData = tableData.map(row => [...row]);
      const currentValue = newData[editingCell.row][editingCell.col];
      const isMerged = currentValue.startsWith('$MERGE ');
      newData[editingCell.row][editingCell.col] = isMerged ? `$MERGE ${editValue}` : editValue;
      updateTableData(newData);
    }
    const cellKey = `${row}-${col}`;
    const cellValue = tableData[row][col];
    setSelectedCells(new Set([cellKey]));
    const cleanValue = cellValue.startsWith('$MERGE ') ? cellValue.substring(7) : cellValue;
    setEditValue(cleanValue);
    setEditingCell({ row, col });
  };

  const handleCellDoubleClick = (row: number, col: number) => {
    if (disabled) return;
    const cellKey = `${row}-${col}`;
    setSelectedCells(new Set([cellKey]));
    const cellValue = tableData[row][col];
    const cleanValue = cellValue.startsWith('$MERGE ') ? cellValue.substring(7) : cellValue;
    setEditValue(cleanValue);
    setEditingCell({ row, col });
  };

  const commitEditingValue = (data: string[][]) => {
    if (!editingCell) return data;
    const newData = data.map((tableRow) => [...tableRow]);
    const currentValue = newData[editingCell.row][editingCell.col];
    const isMerged = currentValue.startsWith('$MERGE ');
    newData[editingCell.row][editingCell.col] = isMerged ? `$MERGE ${editValue}` : editValue;
    return newData;
  };

  const startEditingCell = (row: number, col: number, data: string[][]) => {
    const cellValue = data[row][col];
    setSelectedCells(new Set([`${row}-${col}`]));
    const cleanValue = cellValue.startsWith('$MERGE ') ? cellValue.substring(7) : cellValue;
    setEditValue(cleanValue);
    setEditingCell({ row, col });
  };

  const handleEditComplete = () => {
    if (skipBlurCommitRef.current) {
      skipBlurCommitRef.current = false;
      return;
    }
    if (editingCell) {
      updateTableData(commitEditingValue(tableData));
      setEditingCell(null);
      setEditValue('');
      setSelectedCells(new Set());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && editingCell) {
      const nextCell = findAdjacentBudgetCell(
        tableData,
        editingCell.row,
        editingCell.col,
        e.shiftKey ? -1 : 1
      );
      if (!nextCell) {
        return;
      }
      e.preventDefault();
      skipBlurCommitRef.current = true;
      const committed = commitEditingValue(tableData);
      updateTableData(committed);
      startEditingCell(nextCell.row, nextCell.col, committed);
      return;
    }
    if (e.key === 'Enter') {
      handleEditComplete();
    } else if (e.key === 'Escape') {
      // On escape, clear editing but keep selection
      setEditingCell(null);
      setEditValue('');
    }
  };

  const addRow = () => {
    if (disabled) return;
    const newRow = new Array(tableData[0].length).fill('');
    updateTableData([...tableData, newRow]);
  };

  const addColumn = () => {
    if (disabled) return;
    const newData = tableData.map(row => [...row, '']);
    updateTableData(newData);
  };

  const deleteRow = (rowIndex: number) => {
    if (disabled || tableData.length <= 1) return; // Don't delete if only one row left
    const newData = tableData.filter((_, index) => index !== rowIndex);
    updateTableData(newData);
    setSelectedCells(new Set()); // Clear selection
    setEditingCell(null);
  };

  const deleteColumn = (colIndex: number) => {
    if (disabled || tableData[0].length <= 1) return; // Don't delete if only one column left
    const newData = tableData.map(row => row.filter((_, index) => index !== colIndex));
    updateTableData(newData);
    setSelectedCells(new Set()); // Clear selection
    setEditingCell(null);
  };

  const mergeCellsVertically = () => {
    if (disabled || selectedCells.size < 2) return;

    const cellPositions = Array.from(selectedCells).map(key => {
      const [row, col] = key.split('-').map(Number);
      return { row, col };
    });
    cellPositions.sort((a, b) => a.row - b.row);
    const newData = tableData.map(row => [...row]);
    const firstCell = cellPositions[0];

    // Combine text from all selected cells, properly stripping $MERGE prefixes
    const combinedText = cellPositions
      .map(pos => {
        const value = newData[pos.row][pos.col];
        let cleanValue = value;
        if (value.startsWith('$MERGE ')) {
          cleanValue = value.substring(7);
        } else if (value === '$MERGE') {
          cleanValue = '';
        }
        return cleanValue.trim();
      })
      .filter(text => text.length > 0)
      .join(' ');
    newData[firstCell.row][firstCell.col] = `$MERGE ${combinedText}`;

    for (let i = 1; i < cellPositions.length; i++) {
      const pos = cellPositions[i];
      newData[pos.row][pos.col] = '$MERGE';
    }

    updateTableData(newData);
    setSelectedCells(new Set());
  };

  const unmergeCells = () => {
    if (disabled) return;

    const newData = tableData.map(row => [...row]);
    let hasChanges = false;

    selectedCells.forEach(cellKey => {
      const [row, col] = cellKey.split('-').map(Number);
      const cellValue = newData[row][col];

      if (cellValue === '$MERGE') {
        // This is a child merged cell - clear it
        newData[row][col] = '';
        hasChanges = true;
      } else if (cellValue.startsWith('$MERGE ')) {
        // This is a parent cell with merged content - unmerge all children
        const cleanContent = cellValue.substring(7); // Remove the $MERGE prefix
        newData[row][col] = cleanContent;
        // Find and clear all consecutive merged cells below
        for (let r = row + 1; r < newData.length; r++) {
          if (newData[r][col] === '$MERGE') {
            newData[r][col] = '';
            hasChanges = true;
          } else {
            break;
          }
        }
        hasChanges = true;
      }
    });

    if (hasChanges) {
      updateTableData(newData);
      setSelectedCells(new Set());
      setEditingCell(null);
      setEditValue('');
    }
  };

  const isCellMerged = (row: number, col: number) => {
    return tableData[row][col] === '$MERGE';
  };

  const getCellDisplayValue = (row: number, col: number) => {
    const value = tableData[row][col];
    if (value === '$MERGE') return '';
    if (value.startsWith('$MERGE ')) return value.substring(7);
    return value;
  };

  // Get the merged cell span for a given cell
  const getMergedCellSpan = (row: number, col: number) => {
    if (isCellMerged(row, col)) return 0; // This cell is merged into another

    let span = 1;
    // Check how many consecutive cells below are merged
    for (let r = row + 1; r < tableData.length; r++) {
      if (tableData[r][col] === '$MERGE') {
        span++;
      } else {
        break;
      }
    }
    return span;
  };

  const getCellClassName = (row: number, col: number) => {
    const cellKey = `${row}-${col}`;
    const isSelected = selectedCells.has(cellKey);
    const isMerged = isCellMerged(row, col);
    const isHeader = row === 0;
    const isEditing = editingCell?.row === row && editingCell?.col === col;

    let className = 'border border-gray-300 p-2 text-sm relative cursor-pointer transition-colors select-none min-h-[20px]';

    if (isHeader) {
      className += ' bg-gray-50 font-medium';
    }

    if (isSelected) {
      className += ' bg-blue-100 border-2 border-blue-500 z-10';
    }

    if (isMerged) {
      className += ' bg-gray-100 opacity-50';
    }

    if (isEditing) {
      className += ' bg-white';
    }

    if (disabled) {
      className += ' cursor-default';
    }

    // Add vertical alignment for merged cells
    if (!isMerged && getMergedCellSpan(row, col) > 1) {
      className += ' align-middle';
    }

    return className;
  };

  const hasMergeableCells = () => {
    if (selectedCells.size < 2) return false;

    // Don't allow merging if any selected cell is currently being edited
    if (editingCell) {
      const editingCellKey = `${editingCell.row}-${editingCell.col}`;
      if (selectedCells.has(editingCellKey)) {
        return false;
      }
    }

    // Check if we have at least 2 non-merged cells that can be merged
    const nonMergedCells = Array.from(selectedCells).filter(cellKey => {
      const [row, col] = cellKey.split('-').map(Number);
      return !isCellMerged(row, col);
    });

    return nonMergedCells.length >= 2;
  };

  // Check if we have merged cells in selection (including parent cells with merged content)
  const hasMergedCells = () => {
    return Array.from(selectedCells).some(cellKey => {
      const [row, col] = cellKey.split('-').map(Number);
      const cellValue = tableData[row][col];

      // Check for direct merged cells
      if (cellValue === '$MERGE' || cellValue.startsWith('$MERGE ')) {
        return true;
      }

      // Check if this is a parent cell of a merged group (has $MERGE children below)
      for (let r = row + 1; r < tableData.length; r++) {
        if (tableData[r][col] === '$MERGE') {
          return true; // This cell has merged children, so it's a parent of a merged group
        } else {
          break; // Stop at first non-merged cell
        }
      }

      return false;
    });
  };

  // Global mouse up listener
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleMouseUp();
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging]);

  return (
    <div className="insertion-order-table-container">
      {/* Table Container */}
      <div className="relative mx-4 px-2 mt-6">
        {/* Table */}
        <div className="overflow-hidden">
          <table className="w-full border-collapse">
            {/* Column Delete Buttons Row */}
            {!disabled && (
              <thead>
                <tr className="bg-gray-50 min-h-[20px]">
                  <td className="w-8 text-center p-0 border-0 min-h-[20px]">
                    {/* Empty space above row delete buttons */}
                  </td>
                  {tableData[0].map((_, colIndex) => (
                    <td
                      key={colIndex}
                      className="text-center py-1 border-0 min-h-[20px]"
                    >
                      <div className="flex items-center justify-center h-full">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => deleteColumn(colIndex)}
                          className="w-[18px] h-[18px] p-1 bg-gray-200 hover:bg-gray-300 text-black/75 rounded-full flex items-center justify-center"
                          disabled={tableData[0].length <= 1}
                        >
                          <Minus className="!w-[12px] !h-[12px]" />
                        </Button>
                      </div>
                    </td>
                  ))}
                  {/* Add Column Button */}
                  <td className="w-8 text-center p-0 border-0 min-h-[20px]">
                    <div className="flex items-center justify-center h-full ml-2">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={addColumn}
                        className="w-[18px] h-[18px] p-1 bg-gray-200 hover:bg-gray-300 text-black/75 rounded-full flex items-center justify-center"
                      >
                        <Plus className="!w-[12px] !h-[12px]" />
                      </Button>
                    </div>
                  </td>
                </tr>
              </thead>
            )}

            <tbody>
              {tableData.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className="relative"
                >
                  {/* Row Delete Button */}
                  {!disabled && (
                    <td className="w-8 bg-gray-50 text-center p-0 border-0 min-h-[20px]">
                      <div className="flex items-center justify-center h-full">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => deleteRow(rowIndex)}
                          className="w-[18px] h-[18px] p-1 bg-gray-200 hover:bg-gray-300 text-black/75 rounded-full flex items-center justify-center"
                          disabled={tableData.length <= 1}
                        >
                          <Minus className="!w-[12px] !h-[12px]" />
                        </Button>
                      </div>
                    </td>
                  )}

                  {row.map((cell, colIndex) => {
                    const span = getMergedCellSpan(rowIndex, colIndex);
                    if (span === 0) return null; // Skip merged cells

                    return (
                      <td
                        key={`${rowIndex}-${colIndex}`}
                        className={getCellClassName(rowIndex, colIndex)}
                        rowSpan={span}
                        onMouseDown={(e) => handleMouseDown(rowIndex, colIndex, e)}
                        onMouseEnter={() => handleMouseEnter(rowIndex, colIndex)}
                        onClick={() => handleCellClick(rowIndex, colIndex)}
                        onDoubleClick={() => handleCellDoubleClick(rowIndex, colIndex)}
                        style={span > 1 ? { minHeight: `${span * 20}px` } : undefined}
                      >
                        {editingCell?.row === rowIndex && editingCell?.col === colIndex ? (
                          <input
                            ref={inputRef}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={(e) => {
                              handleEditComplete();
                            }}
                            onKeyDown={handleKeyDown}
                            className="w-full h-full bg-transparent text-sm outline-none border-none p-0 m-0 caret-blue-500 min-h-[20px]"
                            style={{
                              font: 'inherit',
                              color: 'inherit',
                            }}
                            placeholder="Enter text..."
                          />
                        ) : (
                          <div className="flex items-center justify-start h-full min-h-[20px]">
                            <span>
                              {getCellDisplayValue(rowIndex, colIndex)}
                            </span>
                          </div>
                        )}
                      </td>
                    );
                  })}

                  {/* Empty cell for add column button alignment */}
                  {!disabled && (
                    <td className="w-8 border-0" />
                  )}
                </tr>
              ))}

              {/* Add Row Button */}
              {!disabled && (
                <tr className="min-h-[20px]">
                  <td className="w-8 bg-gray-50 text-center p-0 border-0 min-h-[20px]">
                    <div className="flex items-center justify-center h-full mt-2">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={addRow}
                        className="w-[18px] h-[18px] p-1 bg-gray-200 hover:bg-gray-300 text-black/75 rounded-full flex items-center justify-center"
                      >
                        <Plus className="!w-[12px] !h-[12px]" />
                      </Button>
                    </div>
                  </td>
                  {tableData[0].map((_, colIndex) => (
                    <td
                      key={colIndex}
                      className="border-0 min-h-[20px]"
                    />
                  ))}
                  <td className="w-8 border-0 min-h-[20px]" />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {!disabled && (
        <div className="mx-4 px-2 pl-10 text-left flex items-center gap-4 min-h-[40px]">
          <span className="text-sm text-gray-500">
            * Click and drag vertically to select cells for merging.
          </span>

          {selectedCells.size > 0 && (
            <div className="flex gap-2">
              {hasMergeableCells() && (
                <Button
                  type="button"
                  variant="outline"
                  size="med"
                  onClick={mergeCellsVertically}
                  className="flex items-center gap-1"
                >
                  <Merge className="w-4 h-4" />
                  Merge Cells (
                  {selectedCells.size}
                  )
                </Button>
              )}

              {hasMergedCells() && (
                <Button
                  type="button"
                  variant="outline"
                  size="med"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    unmergeCells();
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  className="flex items-center gap-1"
                >
                  <Split className="w-4 h-4" />
                  Unmerge Cells
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};